import { PLAN_FEATURE_INFO, PLAN_TIER_INFO, lowestTierWith, type PlanFeature } from "@gf/shared";
import { buttonClass, cx } from "@gf/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Lock } from "lucide-react";
import { useEffect, useId, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { ME_KEY, useAuth } from "../../lib/auth";
import { useDocumentTitle } from "../../lib/hooks";
import { igAccountsText, joinTierNames, liveCampaignsText, lockReason, lockedFeatureForPath, previousTier, tierAdds, tiersWith, usePlan } from "../../lib/plan";
import { PlanCta } from "./PlanCta";
import "./plans.css";

/**
 * Wraps the signed-in layout's <Outlet/>. On a route that belongs to a plan feature the account
 * doesn't have (PLAN_FEATURE_INFO[f].paths), it renders the upgrade card INSTEAD of the page, so the
 * page never mounts and never fires requests the API would answer with 403 feature_locked.
 * Operators (admins, resellers) and older APIs without entitlements pass straight through.
 */
export function PlanGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { entitlements } = usePlan();
  const locked = lockedFeatureForPath(pathname, entitlements);
  return locked ? <LockedFeaturePage feature={locked} /> : <>{children}</>;
}

function LockedFeaturePage({ feature }: { feature: PlanFeature }) {
  useDocumentTitle(PLAN_FEATURE_INFO[feature].label);
  return (
    <div className="gfp-page">
      <LockedFeature feature={feature} />
    </div>
  );
}

/**
 * "Campaigns is on Growth and Pro": what the feature does, what that plan adds, and how to get it.
 * `embedded` drops the page centring and uses an h2 (inside a Settings tab).
 */
export function LockedFeature({ feature, embedded = false }: { feature: PlanFeature; embedded?: boolean }) {
  const { user } = useAuth();
  const { entitlements } = usePlan();
  const qc = useQueryClient();
  const titleId = useId();
  const info = PLAN_FEATURE_INFO[feature];
  const reason = lockReason(entitlements, feature);
  const Heading = embedded ? "h2" : "h1";

  // The plan may have changed since /me was cached (a reseller just upgraded them): check again,
  // and the real page replaces this card if it's now included.
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: ME_KEY });
  }, [qc, feature]);

  return (
    <section className={cx("plans-locked", embedded && "plans-locked--embedded")} aria-labelledby={titleId}>
      <div className="plans-locked__card">
        <div className="plans-locked__head">
          <span className="plans-locked__icon" aria-hidden="true">
            <Lock size={22} strokeWidth={2} />
          </span>
          <div className="plans-locked__titles">
            <p className="plans-locked__eyebrow">{reason === "upgrade" ? "Not on your plan" : "Switched off"}</p>
            <Heading id={titleId} className="plans-locked__title">
              {reason === "upgrade" ? `${info.label} is on ${joinTierNames(tiersWith(feature))}` : `${info.label} is switched off for your account`}
            </Heading>
          </div>
        </div>
        <p className="plans-locked__blurb">{info.blurb}</p>
        {reason === "upgrade" ? (
          <TierPanel feature={feature} showPrice={!user?.parentId} />
        ) : (
          <p className="plans-locked__note">Your plan includes it, but it's been switched off for this account.</p>
        )}
        <div className="plans-locked__actions">
          <PlanCta reason={reason} />
          <Link to="/plans" className={buttonClass({ variant: "ghost" })}>
            Compare plans
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/** The cheapest plan with the feature, and everything that plan adds over the one below it. */
function TierPanel({ feature, showPrice }: { feature: PlanFeature; showPrice: boolean }) {
  const tier = lowestTierWith(feature);
  const t = PLAN_TIER_INFO[tier];
  const below = previousTier(tier);
  return (
    <div className="plans-locked__tier">
      <div className="plans-locked__tier-top">
        <div>
          <p className="plans-locked__tier-name">{t.name}</p>
          <p className="plans-locked__tier-tagline">{t.tagline}</p>
        </div>
        {showPrice ? (
          <p className="plans-locked__tier-price">
            <strong>${t.priceMonthly}</strong>/mo
          </p>
        ) : null}
      </div>
      <p className="plans-locked__tier-lead">{below ? `Everything in ${PLAN_TIER_INFO[below].name}, plus:` : "Includes:"}</p>
      <ul className="plans-locked__tier-list">
        {tierAdds(tier).map((f) => (
          <li key={f} className={cx(f === feature && "plans-locked__tier-item--this")}>
            <Check size={15} strokeWidth={2.4} aria-hidden="true" />
            <span>{PLAN_FEATURE_INFO[f].label}</span>
          </li>
        ))}
      </ul>
      <p className="plans-locked__tier-limits">
        {igAccountsText(t.igAccounts)} · {liveCampaignsText(t.limits.liveCampaigns)}
      </p>
    </div>
  );
}
